import { Link } from "react-router-dom";
import { ArrowRight, Phone, CheckCircle2, HelpCircle } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { BRAND, IMAGES } from "@/data/site";
import { useT } from "@/i18n/I18nProvider";
import { useSite } from "@/i18n/useSite";
import { PageIntro, SectionHeading, Reveal } from "@/components/Section";
import { TalkCta } from "@/components/TalkCta";

const TILES = ["tile-sky corner-tr", "tile-sun corner-bl", "tile-blue corner-tr", "tile-peach corner-bl"];
const HELP = ["Checking eligibility and gathering evidence", "Completing the Access Request Form", "Preparing for your planning meeting", "Understanding your approved plan", "Connecting you with other providers", "Support coordination and plan reviews"];

const Eligibility = () => {
  const t = useT();
  const { NDIS } = useSite();
  return (
    <section className="py-16 lg:py-24" data-testid="ndis-eligibility">
      <div className="container-x">
        <SectionHeading eyebrow={t("Am I eligible?")} title={t("Who can access the NDIS")} text={t("To become an NDIS participant you generally need to meet all of the following requirements.")} />
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {NDIS.eligibility.map((e, i) => {
            const light = TILES[i].includes("tile-blue");
            return (
              <Reveal key={i} delay={i * 0.07} className="h-full">
                <div className={`${TILES[i]} p-7 h-full`} data-testid={`eligibility-${i}`}>
                  <CheckCircle2 size={28} className={light ? "text-white" : "text-[var(--blue)]"} />
                  <h3 className={`mt-4 text-xl font-semibold ${light ? "!text-white" : "!text-[var(--ink)]"}`}>{e.title}</h3>
                  <p className={`mt-2 text-[15px] leading-relaxed ${light ? "text-white/85" : "text-[var(--ink)]/80"}`}>{e.text}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
        <Reveal delay={0.2}><p className="mt-8 text-sm text-[var(--muted)]">{t("Not sure?")} <Link to="/contact" className="link-u" data-testid="eligibility-contact">{t("Contact us")}</Link> — {t("we'll help you work it out, or visit")} <a href="https://www.ndis.gov.au/applying-access-ndis" target="_blank" rel="noreferrer" className="link-u">ndis.gov.au</a>.</p></Reveal>
      </div>
    </section>
  );
};

const HowToApply = () => {
  const t = useT();
  const { NDIS } = useSite();
  return (
    <section className="py-16 lg:py-24 bg-[var(--blue-tint)]" data-testid="ndis-steps">
      <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-4">
          <SectionHeading eyebrow={t("How to apply")} title={t("Six steps to becoming a participant")} text={t("Applying can feel daunting. Here's what to expect — and we can walk alongside you at every step.")} />
          <Reveal delay={0.1} className="mt-8"><img src={IMAGES.coordination} alt={t("Planning an NDIS application together")} className="w-full h-64 object-cover" style={{ borderRadius: "0 64px 0 64px" }} /></Reveal>
        </div>
        <ol className="lg:col-span-8 space-y-4">
          {NDIS.steps.map((s, i) => (
            <Reveal key={i} delay={i * 0.05}>
              <li className="bg-white corner-tr-sm p-6 flex gap-5" data-testid={`ndis-step-${i}`}>
                <span className="w-11 h-11 rounded-md bg-[var(--coral)] text-white font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                <div><h3 className="text-lg font-semibold !text-[var(--blue)]">{s.title}</h3><p className="mt-1.5 text-[15px] text-[var(--body)] leading-relaxed">{s.text}</p></div>
              </li>
            </Reveal>
          ))}
        </ol>
      </div>
    </section>
  );
};

const PlanManagement = () => {
  const t = useT();
  const { NDIS } = useSite();
  return (
    <section className="py-16 lg:py-24" data-testid="ndis-management">
      <div className="container-x">
        <SectionHeading eyebrow={t("Managing your plan")} title={t("Three ways your NDIS funding can be managed")} text={t("You choose how your plan is managed. Aid in Care works with all three.")} />
        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
          {NDIS.management.map((m, i) => (
            <Reveal key={i} delay={i * 0.08} className="h-full">
              <div className="bg-white border-2 border-[var(--blue-tint)] corner-tr p-7 h-full" data-testid={`management-${i}`}>
                <h3 className="text-xl font-semibold !text-[var(--blue)]">{m.title}</h3>
                <p className="mt-2 text-[15px] text-[var(--body)] leading-relaxed">{m.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const HowWeHelp = () => {
  const t = useT();
  return (
    <section className="py-16 lg:py-24 bg-[var(--blue)] text-white" data-testid="ndis-help">
      <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7">
          <SectionHeading light eyebrow={t("How Aid in Care can help")} title={t("We can assist with your NDIS application")} text={t("Not everyone who could benefit from the NDIS has access to it. If you need help with your access request, understanding your plan or getting the most from your funding, our team can guide you step by step.")} />
          <Reveal delay={0.1}>
            <ul className="mt-8 grid sm:grid-cols-2 gap-3 text-[15px]">
              {HELP.map((h) => (
                <li key={h} className="flex items-start gap-3"><CheckCircle2 size={18} className="text-[var(--sun)] mt-0.5 shrink-0" />{t(h)}</li>
              ))}
            </ul>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/contact" className="btn btn-coral" data-testid="ndis-contact">{t("Ask us about the NDIS")} <ArrowRight size={16} /></Link>
              <a href={`tel:${BRAND.phone.tel}`} className="btn btn-outline-light"><Phone size={16} /> {BRAND.phone.number}</a>
            </div>
          </Reveal>
        </div>
        <Reveal delay={0.1} className="lg:col-span-5">
          <img src={IMAGES.lifeSkills} alt={t("Support worker with participant")} className="w-full h-[320px] lg:h-[420px] object-cover" style={{ borderRadius: "0 80px 0 80px" }} />
        </Reveal>
      </div>
    </section>
  );
};

const Faqs = () => {
  const t = useT();
  const { NDIS } = useSite();
  return (
    <section className="py-16 lg:py-24" data-testid="ndis-faqs">
      <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-4"><SectionHeading eyebrow={t("Common questions")} title={t("NDIS frequently asked questions")} /></div>
        <Reveal className="lg:col-span-8">
          <Accordion type="single" collapsible className="divide-y divide-slate-200 border-y border-slate-200">
            {NDIS.faqs.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border-0">
                <AccordionTrigger className="text-start text-base sm:text-lg font-semibold text-[var(--blue)] hover:no-underline py-5" data-testid={`faq-trigger-${i}`}>
                  <span className="flex items-start gap-3"><HelpCircle size={20} className="text-[var(--coral)] mt-0.5 shrink-0" />{f.q}</span>
                </AccordionTrigger>
                <AccordionContent className="text-[15px] text-[var(--body)] leading-relaxed ps-8 pb-5" data-testid={`faq-content-${i}`}>{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Reveal>
      </div>
    </section>
  );
};

export default function Ndis() {
  const t = useT();
  return (
    <>
      <PageIntro eyebrow={t("The NDIS")} title={t("Understanding the NDIS,")} accent={t("and how to apply.")} text={t("The National Disability Insurance Scheme (NDIS) provides funding to eligible people with disability so they can access the supports they need to live more independently. Here's how it works, who's eligible, how to apply — and how we can help.")} image={IMAGES.community}>
        <div className="mt-8 flex flex-wrap gap-3">
          <a href="#how-to-apply" className="btn btn-coral" data-testid="ndis-how-to-apply">{t("How to apply")} <ArrowRight size={16} /></a>
          <Link to="/contact" className="btn btn-ghost-blue" data-testid="ndis-enquire">{t("Talk to us")}</Link>
        </div>
      </PageIntro>
      <Eligibility />
      <div id="how-to-apply" />
      <HowToApply />
      <PlanManagement />
      <HowWeHelp />
      <Faqs />
      <TalkCta sourcePage="ndis" />
    </>
  );
}
